import React, { useState } from 'react';
import MainLayout from '../components/layout/MainLayout';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import SEO from '../components/common/SEO';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { handleStripeCheckout } from '../lib/stripe';

const ResourceCard = ({ title, category, description, whoItIsFor, problemItSolves, ctaText, ctaLink, isExternal = false, isPaid = false, price = "Free", stripePriceId, nextStepText, nextStepLink, nextStepIsExternal = false, bullets = [] }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const onPurchaseClick = async () => {
    if (isPaid && !agreed) return;
    if (!isPaid) {
      if (isExternal) window.open(ctaLink, '_blank');
      else window.location.hash = ctaLink;
      return;
    }
    setIsLoading(true);
    try {
      await handleStripeCheckout({
        priceId: stripePriceId,
        metadata: { product_name: title }
      });
    } catch (err) {
      alert(`Payment Error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-[2.5rem] overflow-hidden shadow-sm border border-gray-100 flex flex-col hover:shadow-md transition-all duration-300 group h-full">
      <div className="p-8 flex flex-col flex-grow">
        <div className="flex justify-between items-start mb-6">
          <span className="bg-brand-teal/10 text-brand-teal text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest">
            {category}
          </span>
          <span className="font-bold text-brand-navy text-sm">{price}</span>
        </div>
        <h3 className="text-2xl font-bold text-brand-navy mb-3 leading-tight group-hover:text-brand-teal transition-colors">
          {title}
        </h3>
        <p className="text-gray-700 font-medium mb-6 text-sm leading-relaxed italic">
          {description}
        </p>
        <div className="space-y-4 mb-8 flex-grow">
          <div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Who it is for:</span>
            <p className="text-brand-navy font-bold text-sm">{whoItIsFor}</p>
          </div>
          <div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">How it builds confidence:</span>
            {bullets.length > 0 ? (
              <ul className="space-y-1 mt-2">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                    <span className="text-brand-teal font-bold">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-600 text-sm leading-relaxed">{problemItSolves}</p>
            )}
          </div>
        </div>
        {isPaid && (
          <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
            <label className="flex items-start gap-2 cursor-pointer">
              <input 
                type="checkbox" 
                checked={agreed} 
                onChange={() => setAgreed(!agreed)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-teal" 
              />
              <span className="text-[10px] text-gray-500 leading-snug select-none">
                I confirm supply of digital content begins immediately and I lose my right to cancel. I agree to the <a href="/#/terms" className="text-brand-teal font-bold hover:underline">Terms</a>.
              </span>
            </label>
          </div>
        )}
        <Button 
          variant={isPaid ? "primary" : "outline"} 
          fullWidth 
          className={`py-4 text-sm font-bold rounded-2xl ${isPaid && !agreed ? 'opacity-50 grayscale cursor-not-allowed' : ''}`}
          onClick={onPurchaseClick}
          disabled={isLoading || (isPaid && !agreed)}
        >
          {isLoading && <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2" />}
          {ctaText}
        </Button>
        <div className="mt-8 pt-6 border-t border-gray-50 text-center">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-2">Next Step:</span>
          {nextStepIsExternal ? (
            <a href={nextStepLink} target="_blank" rel="noopener noreferrer" className="text-brand-teal font-bold text-xs hover:text-brand-navy transition-colors flex items-center justify-center gap-1">
              {nextStepText} <SafeIcon icon={FiIcons.FiArrowRight} className="w-3 h-3" />
            </a>
          ) : (
            <Button to={nextStepLink} variant="ghost" className="text-brand-teal font-bold text-xs p-0 h-auto hover:bg-transparent hover:text-brand-navy">
              {nextStepText} <SafeIcon icon={FiIcons.FiArrowRight} className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const Resources = () => {
  const clarityLink = "https://tidycal.com/1x8odpm/ai-clarity-session-1y6rlrd-mpnrkpl";
  
  const resources = [
    {
      title: "Free AI Leadership Health Check",
      category: "Assessments",
      description: "Find out how prepared your organisation is to lead AI with confidence.",
      whoItIsFor: "Social impact leaders, CEOs, and senior teams.",
      problemItSolves: "Uncertainty about current AI readiness and where to focus leadership attention first.",
      bullets: [
        "Assess your AI leadership level",
        "Personalised strategic report",
        "Practical next steps for your team",
        "SAFE AI Framework recommendations"
      ],
      ctaText: "Take the Health Check",
      ctaLink: "/assessment",
      nextStepText: "Book a Leadership Review to apply results",
      nextStepLink: clarityLink,
      nextStepIsExternal: true
    },
    {
      title: "The Responsible Leader's Guide to AI",
      category: "Leadership",
      description: "A practical introduction to leading AI through the SAFE AI Framework.",
      whoItIsFor: "Leaders who want a clear starting point for building organisational confidence.",
      problemItSolves: "Learn the six leadership principles that help social impact organisations use AI with confidence while protecting trust, people and mission.",
      ctaText: "Download the Guide",
      ctaLink: "/resources/guide",
      nextStepText: "Take the Health Check to see where you stand",
      nextStepLink: "/assessment"
    },
    {
      title: "AI Prompt Pack for Social Impact Organisations",
      category: "Templates",
      description: "A collection of practical prompts designed to build team capability.",
      whoItIsFor: "Staff and teams looking to use AI confidently in their everyday workflows.",
      problemItSolves: "Stop guessing. Use pre-tested prompts to save time on funding bids, reports, and strategy while maintaining human judgment.",
      bullets: [
        "Funding applications & Reporting",
        "Communications & Strategy drafting",
        "Meeting summaries & Project planning"
      ],
      ctaText: "Download the Prompt Pack",
      ctaLink: "/resources",
      isPaid: true,
      price: "£25.00",
      stripePriceId: import.meta.env.VITE_PROMPT_PACK_PRICE_ID,
      nextStepText: "Read 'Start AI Right' for deeper strategy",
      nextStepLink: "/book"
    },
    {
      title: "Start AI Right for Nonprofits",
      category: "Books",
      description: "The leadership guide for moving from informal AI use to clear organisational confidence.",
      whoItIsFor: "Leaders ready to lead AI across their entire organisation.",
      problemItSolves: "Transition from ad-hoc experimentation to a professional, board-ready leadership approach.",
      bullets: [
        "Identify where AI already exists",
        "Decide where AI belongs",
        "Protect your people and data",
        "Create simple organisational guidance"
      ],
      ctaText: "Learn More",
      ctaLink: "/book",
      nextStepText: "Book an AI Leadership Review",
      nextStepLink: clarityLink,
      nextStepIsExternal: true
    }
  ];

  return (
    <MainLayout>
      <SEO 
        title="Practical Resources for AI Leaders" 
        description="Practical toolkits, prompt packs, and guides for social impact leaders looking to lead AI safely and with confidence." 
        canonical="/resources" 
      />
      
      {/* Hero Section */}
      <Section bg="cream" className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-brand-navy mb-8 tracking-tight">
            Practical resources for leaders leading AI.
          </h1>
          <p className="text-xl text-gray-700 leading-relaxed max-w-2xl mx-auto font-medium">
            Whether you're exploring AI for the first time or building confidence across your organisation, these resources help you make confident decisions and keep people at the centre of AI.
          </p>
        </div>
      </Section>

      {/* Resource Grid */}
      <Section bg="white" className="py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {resources.map((res, i) => (
            <ResourceCard key={i} {...res} />
          ))}
        </div>
      </Section>

      {/* Closing CTA */}
      <Section bg="cream" className="py-24 border-y border-gray-100">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-brand-navy mb-6">Not sure where to begin?</h2>
          <p className="text-lg text-gray-600 mb-10 leading-relaxed">
            Every organisation is at a different stage of its AI journey. If you're unsure which resource is right for you, start with the AI Leadership Health Check or book an AI Leadership Review for direct advice.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button to="/assessment" variant="primary" className="text-lg px-8 py-4 shadow-sm">
              Take the AI Leadership Health Check
            </Button>
            <Button href={clarityLink} variant="outline" className="text-lg px-8 py-4 bg-white">
              Book your AI Leadership Review
            </Button>
          </div>
        </div>
      </Section>
    </MainLayout>
  );
};

export default Resources;